import { ContactJson } from 'app/types/entities'
import { BoardColumn } from './boardColumnSource'
import { NOT_STARTED_STAGE } from './systemPipelines'

/**
 * One column's worth of a board: what is on screen, and how that relates to
 * what is actually out there.
 *
 * `total` is the whole point of keeping this separate from `contacts.length`:
 * a board loads a column in pages, so the two numbers agree only when the
 * column has been read to the end.
 */
export type BoardLane = {
  column: BoardColumn
  contacts: ContactJson[]
  /** The true size of this column, not the number loaded. */
  total: number
  hasMore: boolean
}

/**
 * The lane a board's un-picked-up cards rest in.
 *
 * A real stage now (see `systemPipelines.ts`), not a synthetic column: a
 * contact the queue puts on a board is enrolled at this stage, so `columnOf`
 * resolves to it like any other and a move onto it is an ordinary stage
 * write. The lane still absorbs queue members whose enrolment has not landed
 * yet, which is the one thing that keeps a board honest while it catches up.
 */
export const NOT_STARTED: BoardColumn = { key: NOT_STARTED_STAGE, label: NOT_STARTED_STAGE }

/** Columns a board renders as lanes. Exits are drop targets, never lanes. */
export function laneColumns(columns: readonly BoardColumn[]): BoardColumn[] {
  return columns.filter((column) => !column.exit)
}

/** Columns that take a card off the board rather than resting it. */
export function exitColumns(columns: readonly BoardColumn[]): BoardColumn[] {
  return columns.filter((column) => column.exit)
}

/**
 * Lead-status boards fetch one query per column, so each lane already arrives
 * with its own contacts, total and hasMore attached. Nothing to group here,
 * this only drops the exit columns, which were never fetched in the first
 * place because a card leaving the board has nothing to page through.
 */
export function leadStatusLanes(
  columns: readonly BoardColumn[],
  perColumn: ReadonlyMap<string, { contacts: ContactJson[]; total: number; hasMore: boolean }>,
): BoardLane[] {
  return laneColumns(columns).map((column) => {
    const data = perColumn.get(column.key)
    return { column, contacts: data?.contacts ?? [], total: data?.total ?? 0, hasMore: data?.hasMore ?? false }
  })
}

/**
 * Groups a pipeline board's one membership fetch into stage lanes, with
 * everyone else folded into a synthetic Not started lane.
 *
 * Membership is rare enough (73 of 52,193 contacts at Hamblin) that `members`
 * is a single complete fetch: every stage lane is exact and never paginates,
 * so `hasMore` is always false there and `total` is just how many members
 * landed on that stage. Not started is the only lane that can be partial. Its
 * `total` is derived (the queue's own total minus how many are members)
 * rather than counted off the page in hand, because the page is capped and
 * the pipeline is not. Its `contacts` drop anyone the members fetch already
 * placed on a stage, so a member who happens to land on the ordinary query's
 * first page is never drawn twice.
 */
export function pipelineLanes(opts: {
  columns: readonly BoardColumn[]
  columnOf: (contact: ContactJson) => string | undefined
  members: ContactJson[]
  /** The queue's own total, from its ordinary (unfiltered-by-pipeline) query. */
  queueTotal: number
  notStartedPage: ContactJson[]
  notStartedHasMore: boolean
}): BoardLane[] {
  const stageColumns = laneColumns(opts.columns)
  const memberIds = new Set(opts.members.map((contact) => contact.id))
  // Queue members whose enrolment has not landed yet. They belong in Not
  // started either way: that is the stage they are being enrolled at, and
  // dropping them until the write returns would make the board flicker
  // through an emptier version of itself on every read.
  const unenrolled = opts.notStartedPage.filter((contact) => !memberIds.has(contact.id))
  return stageColumns.map((column) => {
    const members = opts.members.filter((contact) => opts.columnOf(contact) === column.key)
    if (column.key !== NOT_STARTED.key) {
      return { column, contacts: members, total: members.length, hasMore: false }
    }
    // Not started is the only lane that can be partial, and its total is the
    // queue's own minus everyone already standing on a later stage, rather
    // than counted off the page in hand: the page is capped, the queue is not.
    const laterStages = opts.members.length - members.length
    return {
      column,
      contacts: [...members, ...unenrolled],
      total: Math.max(0, opts.queueTotal - laterStages),
      hasMore: opts.notStartedHasMore,
    }
  })
}
